/* eslint-disable react/destructuring-assignment */

import clsx from "clsx"
import type { ClassValue } from "clsx"
import React, { type JSX } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"

import Button from "./Button"
import PageGrid from "./PageGrid"

export type CTACardProps = {
  theme?: string
  backgroundColor?: string
  className?: string
  eyebrow?: string
  heading: string
  copy: string
  image: any | JSX.Element
  buttonText: string
  buttonLink: string
  buttonClassName?: string
  subtext?: any | JSX.Element
  secondCopy?: string
  pebbleBackgroundColor?: string
  isCarousel?: boolean
  target?: string
}
/**
 * Functional component for CTA Card.
 *
 * @component
 * @param theme - color and typography theme can be dark, light or sand
 * @param backgroundColor - background color of the section tag
 * @param className - if for some reason you need to put a class on the section tag
 * @param eyebrow - the small text that shows above the h2 tag
 * @param heading - h2 text
 * @param copy - the paragraph text below the h2
 * @param secondCopy - the paragraph text below the paragraph
 * @param image - the background image url is now a StaticImage that needs to be passed. Moved from static directory to src.
 * @param buttonText - the text for the CTA button
 * @param buttonLink - the url for the CTA button
 * @param buttonClassName - button class for the CTA button for GA tracking
 * @param subtext - optional small copy or link that will show below the main CTA
 * @param pebbleBackgroundColor - optional override of the main text area background (no longer have pebble, need to rename prop)
 */
const CTACard: React.FC<CTACardProps> = (props: CTACardProps) => {
  const {
    theme = "light",
    backgroundColor = "bg-gray-90",
    className = "",
    target = "",
  } = props
  const eyebrowClasses: ClassValue[] = ["mb-4 caption2"]
  const headingClasses: ClassValue[] = ["mb-4"]
  const copyClasses: ClassValue[] = ["body3 mb-8"]
  let pebbleBgColor: string = "bg-green-60"
  if (theme === "light") {
    eyebrowClasses.push("text-gray-70")
    if (props?.eyebrow) {
      headingClasses.push("text-gray-90 heading1")
    } else {
      headingClasses.push("text-gray-90 xl:headline5 heading1")
    }
    copyClasses.push("text-gray-80")
    pebbleBgColor = "bg-gray-10"
  }
  if (theme === "dark") {
    headingClasses.push("text-gray-2.5 xl:headline5 heading1")
    copyClasses.push("text-gray-2.5")
    pebbleBgColor = "bg-gray-80"
  }
  if (theme === "sand") {
    headingClasses.push("text-sand-2.5 xl:headline5 heading1")
    copyClasses.push("text-sand-2.5")
    pebbleBgColor = "bg-sand-80"
  }

  return (
    <section
      className={clsx(
        className,
        backgroundColor,
        props?.isCarousel
          ? ""
          : "xl:py-28 lg:py-16 md:py-9 py-10 col-span-full",
      )}
    >
      <PageGrid className="relative overflow-hidden rounded-lg">
        <div className="relative z-20 md:col-span-2 col-span-full lg:col-span-7 xl:col-span-7">
          <div
            className={clsx(
              props?.pebbleBackgroundColor
                ? props?.pebbleBackgroundColor
                : pebbleBgColor,
              "pb-11 p-4 md:pb-8 md:p-8 lg:p-16",
            )}
          >
            {props?.eyebrow && (
              <h3 className={clsx(eyebrowClasses)}>{props?.eyebrow}</h3>
            )}
            <h2 className={clsx(headingClasses)}>{props?.heading}</h2>
            <p className={clsx(copyClasses)}>{props?.copy}</p>
            {props?.secondCopy && (
              <p className={clsx(copyClasses)}>{props?.secondCopy}</p>
            )}
            <Button
              to={props?.buttonLink}
              target={target}
              className={clsx(props?.buttonClassName)}
            >
              {props?.buttonText}
            </Button>
            {props?.subtext && (
              <div className="mt-5 text-black-100">
                <Markdown remarkPlugins={[remarkGfm]}>{props.subtext}</Markdown>
              </div>
            )}
          </div>
        </div>
        <div className="h-[242px] md:h-auto relative block col-span-full md:col-span-2 lg:col-span-5 xl:col-span-5">
          <div
            title={props.image.title}
            style={{ backgroundImage: `url(${props.image.url})` }}
            className="!absolute bottom-0 md:ml-[-25%] md:top-0 left-0 right-0 h-[242px] w-full md:h-full z-10 md:w-[150%] bg-cover bg-center"
          />
        </div>
      </PageGrid>
    </section>
  )
}

export default CTACard
